import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@vibe/core';
import type { WorklogReportResponse } from '../../api/report.api';
import { getTimesheetConfig } from '../../api/timesheet-approval.api';
import {
  createWorkbook, addTitleRow, styleHeaderRow, styleGroupRow, styleDataRow, styleTotalRow,
  setColumnWidths, downloadWorkbook, fillCell, getStatusColors, formatDateForExcel, round,
  hexToArgb, COLORS,
} from '../../utils/export-report-excel';

interface Props {
  data: WorklogReportResponse;
}

const WorklogReportExportButton: React.FC<Props> = ({ data }) => {
  const [approvalEnabled, setApprovalEnabled] = useState<boolean | null>(null);
  useEffect(() => {
    let cancelled = false;
    getTimesheetConfig()
      .then((cfg) => {
        if (!cancelled) setApprovalEnabled(cfg.approval_enabled);
      })
      .catch(() => {
        if (!cancelled) setApprovalEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleExport = useCallback(async () => {
    try {
    const { groups, summary } = data;
    const showApproval = approvalEnabled === true;

    const wb = createWorkbook();
    const ws = wb.addWorksheet('Worklog Report');

    addTitleRow(ws, 'Worklog Report', data.period.startDate, data.period.endDate);

    // Header row — approval columns only when the approval workflow is enabled.
    const headers = showApproval
      ? [
          'Status', 'Approved by', 'Person', 'Date', 'Board', 'Item',
          'Category', 'Billable', 'Time Spent (h)', 'Comment',
        ]
      : [
          'Person', 'Date', 'Board', 'Item',
          'Category', 'Billable', 'Time Spent (h)', 'Comment',
        ];
    const headerRow = ws.addRow(headers);
    styleHeaderRow(ws, headerRow);

    // Column widths — match headers order.
    const columnWidths = showApproval
      ? [16, 18, 18, 12, 22, 22, 16, 10, 14, 30]
      : [18, 12, 22, 22, 16, 10, 14, 30];
    setColumnWidths(ws, columnWidths);

    // Column indexes (1-based) shift depending on whether approval columns are shown.
    const dateCol = showApproval ? 4 : 2;
    const billableCol = showApproval ? 8 : 6;
    const hoursCol = showApproval ? 9 : 7;

    for (const group of groups) {
      if (groups.length > 1 || group.groupName !== 'All Worklogs') {
        // Aggregate per-group totals to mirror the web view's inline group header.
        let groupTotalHours = 0;
        let groupBillableHours = 0;
        let groupNonBillableHours = 0;
        for (const r of group.rows) {
          groupTotalHours += r.hours;
          if (r.categoryIsBillable === true) groupBillableHours += r.hours;
          else groupNonBillableHours += r.hours;
        }

        const groupCells = new Array(headers.length).fill('');
        groupCells[0] = group.groupName;
        // Put the combined totals label in the Billable column and the total
        // hours in the Hours column so the summary lines up with the data columns.
        groupCells[billableCol - 1] =
          `${group.rows.length} worklog${group.rows.length === 1 ? '' : 's'} · ` +
          `${round(groupBillableHours)}h billable · ${round(groupNonBillableHours)}h non-billable`;
        groupCells[hoursCol - 1] = round(groupTotalHours);
        const groupRow = ws.addRow(groupCells);
        styleGroupRow(groupRow);
        groupRow.getCell(hoursCol).numFmt = '0.00';
      }

      for (const row of group.rows) {
        const statusLabel = row.approvalStatus
          ? row.approvalStatus === 'approved'
            ? 'Closed Approved'
            : row.approvalStatus === 'rejected'
              ? 'Closed Rejected'
              : row.approvalStatus.charAt(0).toUpperCase() + row.approvalStatus.slice(1)
          : '';

        const rowValues = showApproval
          ? [
              statusLabel,
              row.approverName ?? '',
              row.userName,
              formatDateForExcel(row.date),
              row.boardName,
              row.itemName,
              row.categoryName ?? '',
              row.categoryIsBillable != null ? (row.categoryIsBillable ? 'Yes' : 'No') : '',
              round(row.hours),
              row.description ?? '',
            ]
          : [
              row.userName,
              formatDateForExcel(row.date),
              row.boardName,
              row.itemName,
              row.categoryName ?? '',
              row.categoryIsBillable != null ? (row.categoryIsBillable ? 'Yes' : 'No') : '',
              round(row.hours),
              row.description ?? '',
            ];
        const excelRow = ws.addRow(rowValues);
        styleDataRow(excelRow);

        // Color status cell (only when approval columns are rendered)
        if (showApproval) {
          const statusColors = getStatusColors(row.approvalStatus);
          if (statusColors) {
            fillCell(excelRow.getCell(1), statusColors.bg, statusColors.font, true);
          }
        }

        // Color billable cell (green for billable, orange for non-billable — matching web)
        if (row.categoryIsBillable === true) {
          fillCell(excelRow.getCell(billableCol), COLORS.statusApprovedBg, COLORS.statusApprovedFont, true);
        } else if (row.categoryIsBillable === false) {
          fillCell(excelRow.getCell(billableCol), COLORS.nonBillableBg, COLORS.nonBillableFont);
        }

        // Center-align date and hours
        excelRow.getCell(dateCol).alignment = { horizontal: 'center', vertical: 'middle' };
        excelRow.getCell(hoursCol).alignment = { horizontal: 'center', vertical: 'middle' };
        excelRow.getCell(hoursCol).numFmt = '0.00';
      }
    }

    // Summary row — column indexes follow the same shift as data rows.
    // Columns when approval shown: [Status, Approved by, Person, Date, Board, Item, Category, Billable, Hours, Comment]
    // Columns when approval hidden:                      [Person, Date, Board, Item, Category, Billable, Hours, Comment]
    const personCol = showApproval ? 3 : 1;
    const itemCol = showApproval ? 6 : 4;

    const summaryCells = new Array(headers.length).fill('');
    summaryCells[0] = 'Total';
    summaryCells[personCol - 1] = `${summary.totalUsers} users`;
    summaryCells[itemCol - 1] = `${summary.totalWorklogs} worklogs`;
    summaryCells[hoursCol - 1] = round(summary.totalHours);
    const summaryRow = ws.addRow(summaryCells);
    styleTotalRow(summaryRow);
    summaryRow.getCell(hoursCol).numFmt = '0.00';

    // Billable breakdown row
    const breakdownCells = new Array(headers.length).fill('');
    breakdownCells[billableCol - 1] = 'Billable:';
    breakdownCells[hoursCol - 1] = round(summary.totalBillableHours);
    const breakdownRow = ws.addRow(breakdownCells);
    breakdownRow.font = { size: 9, color: { argb: hexToArgb(COLORS.secondaryText) } };
    breakdownRow.getCell(hoursCol).numFmt = '0.00';
    fillCell(breakdownRow.getCell(billableCol), COLORS.statusApprovedBg, COLORS.statusApprovedFont, true);
    breakdownRow.getCell(billableCol).font = { size: 9, bold: true, color: { argb: hexToArgb(COLORS.statusApprovedFont) } };

    const nonBillableCells = new Array(headers.length).fill('');
    nonBillableCells[billableCol - 1] = 'Non-billable:';
    nonBillableCells[hoursCol - 1] = round(summary.totalNonBillableHours);
    const nonBillableRow = ws.addRow(nonBillableCells);
    nonBillableRow.font = { size: 9, color: { argb: hexToArgb(COLORS.secondaryText) } };
    nonBillableRow.getCell(hoursCol).numFmt = '0.00';

    await downloadWorkbook(wb, `worklog_report_${data.period.startDate}_${data.period.endDate}.xlsx`);
    } catch (err) {
      console.error('Excel export failed:', err);
    }
  }, [data, approvalEnabled]);

  return (
    <Button
      kind={Button.kinds.SECONDARY}
      size={Button.sizes.SMALL}
      onClick={handleExport}
      disabled={approvalEnabled === null}
    >
      Export Excel
    </Button>
  );
};

export default WorklogReportExportButton;
